import React, { useCallback, useState } from 'react';
import type { CustomCellEditorProps } from 'ag-grid-react';
import { useSelector } from 'react-redux';
import moment, { type Moment } from 'moment';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { currentCompanyDateFormatSelector } from 'domain/companies';
import { CellEditorWrapper } from './StyledComponents';

const ISO_DATE = 'YYYY-MM-DD';

/**
 * Inline date editor for the vendor-level "Valid Until" column.
 * Past dates are disabled (the value cannot be earlier than today). Commits an ISO
 * `YYYY-MM-DD` string via `onValueChange` + `stopEditing`; bails (reverts) on a past/invalid date.
 */
const ValidUntilDateCellEditor: React.FC<CustomCellEditorProps<unknown, string | null>> = ({
  value,
  onValueChange,
  stopEditing,
}) => {
  const dateFormat = useSelector(currentCompanyDateFormatSelector);
  const [open, setOpen] = useState(true);

  const commit = useCallback(
    (day: Moment | null) => {
      // Refuse empty / invalid / past dates — revert by stopping without a value change.
      if (!day || !day.isValid() || day.isBefore(moment(), 'day')) {
        stopEditing(true);
        return;
      }
      onValueChange(day.format(ISO_DATE));
      stopEditing();
    },
    [onValueChange, stopEditing],
  );

  return (
    <CellEditorWrapper>
      <DesktopDatePicker
        open={open}
        onClose={() => setOpen(false)}
        format={dateFormat}
        value={value ? moment(value) : null}
        onAccept={commit}
        disablePast
        minDate={moment().startOf('day')}
        slotProps={{
          popper: { className: 'ag-custom-component-popup' },
          textField: {
            fullWidth: true,
            autoFocus: true,
            onKeyDown: (e) => {
              if (e.key === 'Escape') stopEditing(true);
            },
            sx: { '& .MuiInputBase-root': { height: 40, backgroundColor: 'background.paper' } },
          },
        }}
      />
    </CellEditorWrapper>
  );
};

export default ValidUntilDateCellEditor;
